/**
 * The personalization surface (SKE-4166, phase 4).
 *
 * Revealed by the phase-3 email unlock and nothing else: giving an email is
 * what buys the visitor the "make this board my client's" moment. The visitor
 * names a client, the board re-skins, and the demo stops being a sample and
 * starts being a proposal.
 *
 * THREE STATES, ONE COMPONENT
 *   PROMPT   the unlock confirmation plus the client input.
 *   PENDING  the input, disabled, while the lookup is in flight.
 *   APPLIED  a compact card naming the brand, with a way back to the input.
 *
 * NEVER A DEAD END. A SILENT failure (offline visitor, unreachable/unconfigured
 * server, a brand nobody has indexed) still personalizes: `resolveDemoBrand`
 * fills name, colour and a monogram logo locally, ending in the APPLIED state.
 * A REJECTION worth a word — invalid input, a blocked domain (a competitor or
 * Sked itself), or a rate limit — instead keeps the input open with a one-line
 * hint and does NOT skin the board. See the "NEVER BROKEN" header in
 * demoBrand.ts.
 *
 * The brand is written into the visitor's own localStorage snapshot, like every
 * other demo mutation — the hard safety invariant at the top of demoApi.ts
 * applies unchanged. Nothing about the visitor's client leaves the browser
 * except the domain itself, which the server needs to look it up.
 *
 * Tailwind only (styled-components are lint-blocked in this app); all motion is
 * behind `motion-safe:`.
 */

import { useCallback, useState } from 'react'

import { Button } from 'ui/button'
import { Input } from 'ui/input'
import { Label } from 'ui/label'

import { trackDemo } from './demoAnalytics'
import { applyDemoBrand } from './demoApi'
import {
  fetchDemoBrand,
  isLikelyBrandInput,
  resolveDemoBrand,
  toBrandSeed,
  type DemoBrandFailureReason,
} from './demoBrand'
import { getDemoSessionId } from './demoAttribution'
import { demoFormat, demoStrings } from './demoStrings'
import { useDemoBrand } from './useDemoBrand'
import { useDemoGate } from './useDemoGate'

/**
 * Only the failures worth a word to the visitor. Everything else — network,
 * server, not-configured — personalizes silently from the local fallbacks,
 * because saying "something went wrong" about a board that visibly just got
 * their client's name on it would be a lie that makes the product look broken.
 */
const ERROR_STRINGS: Partial<Record<DemoBrandFailureReason, string>> = {
  'invalid-domain': demoStrings.brandErrorInvalid,
  'blocked-domain': demoStrings.brandErrorBlocked,
  'rate-limited': demoStrings.brandErrorRateLimited,
}

const DemoPersonalizationSlot: React.FC = () => {
  const { isUnlocked } = useDemoGate()
  const brand = useDemoBrand()

  const [website, setWebsite] = useState('')
  const [isApplying, setIsApplying] = useState(false)
  const [hint, setHint] = useState<string | null>(null)
  // After a brand is applied the panel collapses to a summary; this reopens the
  // input so a visitor can try a second client.
  const [isEditing, setIsEditing] = useState(false)

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()

      if (isApplying) {
        return
      }

      const candidate = website.trim()

      if (!isLikelyBrandInput(candidate)) {
        setHint(demoStrings.brandErrorInvalid)
        return
      }

      setHint(null)
      setIsApplying(true)

      const result = await fetchDemoBrand(candidate, getDemoSessionId())
      setIsApplying(false)

      if (!result.ok) {
        trackDemo('demo-personalize-failed', {
          reason: result.reason,
          // The domain is business context the visitor volunteered, not
          // personal data — but only ever the registrable part, never the URL
          // they pasted (which can carry a path and a query string).
          brand_domain: toBrandSeed(candidate),
        })

        // A rejection worth a word (invalid input, a blocked domain — a
        // competitor or Sked itself — or a rate limit) does NOT skin the board:
        // keep the form open with the hint. Skinning here would name a
        // competitor as the hero brand of our own sales asset, or swallow the
        // message. Only SILENT failures (network/server/not-configured) fall
        // through to personalize by name — see NEVER BROKEN.
        const rejectionHint = ERROR_STRINGS[result.reason]
        if (rejectionHint) {
          setHint(rejectionHint)
          return
        }
      }

      // Tokens on success, null on a silent failure — and `resolveDemoBrand`
      // produces a complete, applicable brand either way.
      const resolved = resolveDemoBrand(
        candidate,
        result.ok ? result.tokens : null
      )

      await applyDemoBrand(resolved)
      setIsEditing(false)
      setWebsite('')

      trackDemo('demo-personalized', {
        source: resolved.source,
        brand_domain: resolved.domain,
        is_handle: candidate.startsWith('@'),
      })
    },
    [isApplying, website]
  )

  // Phase 3 owns the gate; this only ever renders behind it.
  if (!isUnlocked) {
    return null
  }

  const isSummary = brand !== null && !isEditing

  return (
    <div
      data-testid="demo-personalization-slot"
      // `max-[906px]:` is Homepage's own mobile breakpoint. Below it the
      // marketer mirror's role toggle owns bottom-left at z-[70]; sit above it
      // rather than under it. Same offset as the gate button this replaces, so
      // unlocking doesn't make the panel jump. Desktop is unchanged.
      className="fixed bottom-4 left-4 z-50 w-[19rem] max-w-[calc(100vw-2rem)] rounded-lg border border-blue-100 bg-white px-4 py-3 shadow-md max-[906px]:bottom-[4.25rem]"
    >
      {isSummary ? (
        <>
          <div className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
              // The one place a brand-supplied colour reaches the DOM. It has
              // been through `sanitiseAccent` (`#rrggbb` or nothing), so it
              // cannot carry a second declaration or a `url()`.
              style={{ backgroundColor: brand.accent }}
            />
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">
              {demoStrings.brandAppliedBadge}
            </p>
          </div>

          <p className="mt-1 font-gotham text-sm font-bold text-neutral-800">
            {demoFormat('brandAppliedTitle', { brand: brand.name })}
          </p>
          <p className="mt-0.5 text-xs text-neutral-500">
            {demoStrings.brandAppliedBody}
          </p>

          {brand.source === 'derived' ? (
            <p className="mt-1 text-xs text-neutral-400">
              {demoStrings.brandDerivedHint}
            </p>
          ) : null}

          <button
            type="button"
            onClick={() => {
              setHint(null)
              setIsEditing(true)
            }}
            className="mt-2 text-xs font-medium text-blue-600 underline-offset-2 hover:underline"
          >
            {demoStrings.brandChangeLabel}
          </button>
        </>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          <p className="font-gotham text-sm font-bold text-neutral-800">
            {demoStrings.brandPanelTitle}
          </p>
          <p className="mt-0.5 text-xs text-neutral-500">
            {demoStrings.brandPanelBody}
          </p>

          <div className="mt-2 flex flex-col gap-1.5">
            <Label htmlFor="demo-brand-input" className="sr-only">
              {demoStrings.brandInputLabel}
            </Label>
            <Input
              id="demo-brand-input"
              name="website"
              type="text"
              autoComplete="off"
              spellCheck={false}
              placeholder={demoStrings.brandInputPlaceholder}
              value={website}
              disabled={isApplying}
              onChange={(event) => {
                setWebsite(event.target.value)
                setHint(null)
              }}
              aria-invalid={hint ? true : undefined}
              aria-describedby={hint ? 'demo-brand-hint' : undefined}
            />

            {hint ? (
              <p
                id="demo-brand-hint"
                role="status"
                className="text-xs text-neutral-500"
              >
                {hint}
              </p>
            ) : null}

            <div className="flex items-center gap-2">
              <Button
                type="submit"
                variant="blue-primary"
                size="sm"
                disabled={isApplying}
              >
                {isApplying
                  ? demoStrings.brandSubmittingLabel
                  : demoStrings.brandSubmitLabel}
              </Button>

              {brand ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setHint(null)
                    setWebsite('')
                    setIsEditing(false)
                  }}
                >
                  {demoStrings.brandCancelLabel}
                </Button>
              ) : null}
            </div>
          </div>
        </form>
      )}
    </div>
  )
}

export default DemoPersonalizationSlot
